import { useState } from "react";
import { useCustom } from "@refinedev/core";
import { CalendarClock, CalendarDays, History, ReceiptText, Sun } from "lucide-react";

import type { DagStatus, PensionKlantDagopvang, PensionReservering, ReserveringStatus } from "../pensionKlantTypes";
import { formatEuro } from "../../i18n/format";
import { groepeerBoekingen, tijd, tijdlijn, type Boeking, type TijdlijnItem } from "./boekingen";
import { LEEG, datumMetJaar, periode, type Context } from "./hulp";
import { Rondje } from "./Tegels";
import { DierLink, Leeg, Pil, Sectie, Verder, type PilToon } from "./Sectie";

/** Zoveel eerdere regels staan er meteen; de rest komt op verzoek. */
const EERST_ZICHTBAAR = 10;

export const RES_TOON: Record<ReserveringStatus, PilToon> = {
  IN_PENSION: "groen",
  GEPLAND: "accent",
  AFGEROND: "grijs",
  GEANNULEERD: "rood",
};

export const DAG_TOON: Record<DagStatus, PilToon> = {
  AANWEZIG: "groen",
  VANDAAG: "groen",
  GEPLAND: "accent",
  OPGEHAALD: "grijs",
  GEWEEST: "grijs",
  GEANNULEERD: "rood",
};

/** De dagopvang van de klant; Refine deelt de aanroep tussen de tabbladen die hem gebruiken. */
export function useDagopvang(klantId: string) {
  return useCustom<PensionKlantDagopvang>({ url: `pension/klanten/${klantId}/dagopvang`, method: "get" });
}

/** "di 26 okt 2021". */
function weekdagDatum(iso: string | null, locale: string): string {
  if (!iso) return LEEG;
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  const dag = new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" }).format(d).replace(".", "");
  return `${dag} ${datumMetJaar(iso, locale).replace(/^0/, "")}`;
}

/** Dag en maand als een blokje, zoals op een kalenderblad. */
function DatumBlok({ datum, locale }: { datum: string | null; locale: string }) {
  if (!datum) return <span className="pk-datumblok" aria-hidden="true" />;
  const d = new Date(`${datum.slice(0, 10)}T00:00:00Z`);
  return (
    <span className="pk-datumblok" aria-hidden="true">
      <span className="pk-datumblok__dag">{d.getUTCDate()}</span>
      <span className="pk-datumblok__maand">
        {new Intl.DateTimeFormat(locale, { month: "short", timeZone: "UTC" }).format(d).replace(".", "")}
      </span>
    </span>
  );
}

// ── Reserveringen ────────────────────────────────────────────────────────────────────────────────────

type Filter = "alles" | "pension" | "dagopvang";

/**
 * Pension en dagopvang op één tijdlijn: wat de klant nog tegoed heeft bovenaan, wat geweest is
 * daaronder. Van de dagopvang staan de dertig dichtstbijzijnde dagen naar voren en naar achteren.
 */
export function ReserveringenTab({
  klantId,
  reserveringen,
  laden,
  ctx,
  onKiesDier,
}: {
  klantId: string;
  reserveringen: PensionReservering[];
  laden: boolean;
  ctx: Context;
  onKiesDier: (id: number) => void;
}) {
  const { t, vandaag } = ctx;
  const [filter, setFilter] = useState<Filter>("alles");
  const [alles, setAlles] = useState(false);
  const { data, isLoading: dagopvangLaadt } = useDagopvang(klantId);
  const lijn = tijdlijn(groepeerBoekingen(reserveringen), data?.data, vandaag);
  const past = (i: TijdlijnItem) => filter === "alles" || i.soort === filter;
  const komend = lijn.komend.filter(past);
  const geweest = lijn.geweest.filter(past);

  if (laden) return <Leeg>{t("common.loading")}</Leeg>;

  return (
    <div className="pk-blad">
      <div className="pk-filter" role="radiogroup" aria-label={t("pensionKlant.reserveringen.filterAria")}>
        {(["alles", "pension", "dagopvang"] as Filter[]).map((f) => (
          <button
            key={f}
            type="button"
            role="radio"
            aria-checked={filter === f}
            className="pk-filter__knop"
            onClick={() => setFilter(f)}
          >
            {t(`pensionKlant.reserveringen.filter.${f}`)}
          </button>
        ))}
        {dagopvangLaadt && filter !== "pension" && <span className="pk-voetnoot">{t("common.loading")}</span>}
      </div>
      <Sectie titel={t("pensionKlant.reserveringen.komend")} icoon={CalendarClock} tint="accent" aantal={komend.length}>
        {komend.length > 0 ? (
          <Tijdlijn items={komend} ctx={ctx} onKiesDier={onKiesDier} />
        ) : (
          <Leeg>{t("pensionKlant.reserveringen.geenKomend")}</Leeg>
        )}
      </Sectie>
      <Sectie titel={t("pensionKlant.reserveringen.geweest")} icoon={History} tint="grijs" aantal={geweest.length}>
        {geweest.length > 0 ? (
          <>
            <Tijdlijn items={alles ? geweest : geweest.slice(0, EERST_ZICHTBAAR)} ctx={ctx} onKiesDier={onKiesDier} />
            {geweest.length > EERST_ZICHTBAAR && (
              <Verder onClick={() => setAlles((x) => !x)}>
                {alles ? t("pensionKlant.blad.minder") : t("pensionKlant.blad.alle", { args: [geweest.length] })}
              </Verder>
            )}
          </>
        ) : (
          <Leeg>{t("pensionKlant.reserveringen.geenGeweest")}</Leeg>
        )}
        {filter !== "pension" && (data?.data?.aantallen.geweest ?? 0) > (data?.data?.geweest.length ?? 0) && (
          <p className="pk-voetnoot">{t("pensionKlant.reserveringen.dagopvangBegrensd")}</p>
        )}
      </Sectie>
    </div>
  );
}

/** Het bedrag van een boeking en hoe het ervoor staat: gefactureerd, betaald, of nog niet gefactureerd. */
function Geld({ boeking, ctx }: { boeking: Boeking; ctx: Context }) {
  const { t, locale } = ctx;
  const f = boeking.factuur;
  const toon: Record<string, PilToon> = { BETAALD: "groen", OPEN: "accent", VERVALLEN: "rood", CONCEPT: "grijs" };
  return (
    <>
      {boeking.totaalBedrag != null && <strong className="pk-dag__bedrag">{formatEuro(boeking.totaalBedrag, locale)}</strong>}
      {f ? (
        <Pil toon={toon[f.status] ?? "grijs"}>
          <ReceiptText size={12} aria-hidden="true" />
          {f.nummer != null
            ? t(`pensionKlant.factuur.nummer.${f.status}`, { args: [f.nummer] })
            : t(`pensionKlant.factuur.status.${f.status}`)}
        </Pil>
      ) : (
        <Pil toon="grijs">
          <ReceiptText size={12} aria-hidden="true" />
          {t("pensionKlant.factuur.geen")}
        </Pil>
      )}
      {f && f.openstaand != null && f.openstaand > 0 && (
        <span className="pk-dag__open">{t("pensionKlant.factuur.openstaand", { args: [formatEuro(f.openstaand, locale)] })}</span>
      )}
    </>
  );
}

function Tijdlijn({ items, ctx, onKiesDier }: { items: TijdlijnItem[]; ctx: Context; onKiesDier: (id: number) => void }) {
  const { t, locale } = ctx;
  return (
    <ul className="pk-dagen">
      {items.map((i) => (
        <li key={i.sleutel} className="pk-dag">
          <DatumBlok datum={i.datum} locale={locale} />
          {i.soort === "pension" ? (
            <>
              <div className="pk-dag__tekst">
                <span className="pk-dag__kop">
                  <Rondje icoon={CalendarDays} tint="accent" />
                  {t("pensionKlant.tab.pension")}
                  <span className="pk-dag__tijd">
                    {periode(i.boeking.van, i.boeking.tot, locale)} ·{" "}
                    {t("pensionKlant.reserveringen.nachten", { args: [i.boeking.nachten] })}
                  </span>
                </span>
                <span className="pk-dierlinks">
                  {i.boeking.dieren.map((d, n) => (
                    <DierLink key={`${d.id ?? "x"}-${n}`} id={d.id} naam={d.naam} onKiesDier={onKiesDier} />
                  ))}
                </span>
              </div>
              <span className="pk-dag__geld">
                <Geld boeking={i.boeking} ctx={ctx} />
              </span>
              <Pil toon={RES_TOON[i.boeking.status]}>{t(`pensionKlant.resstatus.${i.boeking.status}`)}</Pil>
            </>
          ) : (
            <>
              <div className="pk-dag__tekst">
                <span className="pk-dag__kop">
                  <Rondje icoon={Sun} tint="amber" />
                  {t("pensionKlant.tab.dagopvang")}
                  <span className="pk-dag__tijd">
                    {weekdagDatum(i.dag.datum, locale)}
                    {i.dag.van && i.dag.tot && ` · ${tijd(i.dag.van)}–${tijd(i.dag.tot)}`}
                  </span>
                </span>
                <span className="pk-dierlinks">
                  {i.dag.dieren.map((d) => (
                    <DierLink key={d.id} id={d.id} naam={d.naam} onKiesDier={onKiesDier} />
                  ))}
                </span>
              </div>
              <Pil toon={DAG_TOON[i.dag.status]}>{t(`pensionKlant.dagstatus.${i.dag.status}`)}</Pil>
            </>
          )}
        </li>
      ))}
    </ul>
  );
}
